import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import Navigation from '../../../../components/feature/Navigation';
import Footer from '../../../../components/feature/Footer';
import Button from '../../../../components/base/Button';
import { useAuth } from '../../../../hooks/useAuth';
import { GooeyLoader } from '../../../../components/ui/gooey-loader';
import { resolveApiBaseUrl } from '../../../../utils/apiClient';
import { formatAmount, latnLocale } from '../../../../utils/numberFormat';

const API_BASE_URL = resolveApiBaseUrl();

type ComplaintUpdate = {
  id: string;
  fromStatus?: string;
  toStatus: string;
  message: string;
  messageAr?: string;
  isPublic: boolean;
  createdAt: string;
};

type Complaint = {
  id: string;
  category: string;
  status: string;
  priority: string;
  subject: string;
  subjectAr?: string;
  description: string;
  descriptionAr?: string;
  resolutionNotes?: string;
  resolutionNotesAr?: string;
  rejectionReason?: string;
  createdAt: string;
  reviewedAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  satisfactionRating?: number;
  satisfactionComment?: string;
  property?: { id: string; title: string; slug: string; price: number };
  agent?: { id: string; displayName: string; avatarUrl?: string };
  company?: { id: string; name: string; logoUrl?: string };
  attachments: Array<{ id: string; url: string; filename: string }>;
  updates: ComplaintUpdate[];
};

const categoryLabels: Record<string, { en: string; ar: string; icon: string }> = {
  SERVICE_QUALITY: { en: 'Service Quality', ar: 'جودة الخدمة', icon: 'ri-customer-service-2-line' },
  AGENT_BEHAVIOR: { en: 'Agent Behavior', ar: 'سلوك الوكيل', icon: 'ri-user-star-line' },
  PROPERTY_ISSUES: { en: 'Property Issues', ar: 'مشاكل العقار', icon: 'ri-home-gear-line' },
  PAYMENT_BILLING: { en: 'Payment & Billing', ar: 'الدفع والفواتير', icon: 'ri-bank-card-line' },
  TECHNICAL_ISSUES: { en: 'Technical Issues', ar: 'مشاكل تقنية', icon: 'ri-bug-line' },
  OTHER: { en: 'Other', ar: 'أخرى', icon: 'ri-question-line' },
};

const statusConfig: Record<string, { en: string; ar: string; color: string; bgColor: string }> = {
  NEW: { en: 'New', ar: 'جديد', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  UNDER_REVIEW: { en: 'Under Review', ar: 'قيد المراجعة', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  IN_PROGRESS: { en: 'In Progress', ar: 'قيد المعالجة', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  RESOLVED: { en: 'Resolved', ar: 'تم الحل', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  CLOSED: { en: 'Closed', ar: 'مغلق', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  REJECTED: { en: 'Rejected', ar: 'مرفوض', color: 'text-rose-700', bgColor: 'bg-rose-100' },
};

const priorityConfig: Record<string, { en: string; ar: string; color: string }> = {
  LOW: { en: 'Low', ar: 'منخفض', color: 'text-gray-600' },
  MEDIUM: { en: 'Medium', ar: 'متوسط', color: 'text-blue-600' },
  HIGH: { en: 'High', ar: 'عالي', color: 'text-amber-600' },
  URGENT: { en: 'Urgent', ar: 'عاجل', color: 'text-rose-600' },
};

export default function ComplaintDetailPage() {
  const { t, i18n } = useTranslation(['common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user, session } = useAuth();
  const isRTL = i18n.dir() === 'rtl';

  const [complaint, setComplaint] = useState<Complaint | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Rating state
  const [showRating, setShowRating] = useState(searchParams.get('rate') === 'true');
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate(`/signin?redirect=/complaints/my/${id}`);
      return;
    }
    fetchComplaint();
  }, [user, id]);

  const fetchComplaint = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/complaints/my/${id}`, {
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setComplaint(data.data);
      } else {
        setError(data.message || 'Failed to load complaint');
      }
    } catch (err) {
      console.error('Error fetching complaint:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRating = async () => {
    if (!rating) return;

    setSubmittingRating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/complaints/${id}/rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ rating, comment: ratingComment }),
      });

      if (response.ok) {
        await fetchComplaint();
        setShowRating(false);
      }
    } catch (err) {
      console.error('Error submitting rating:', err);
    } finally {
      setSubmittingRating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(latnLocale(isRTL ? 'ar-SA' : 'en-US'), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!user) {
    return null;
  }

  const category = complaint ? categoryLabels[complaint.category] || categoryLabels.OTHER : null;
  const status = complaint ? statusConfig[complaint.status] || statusConfig.NEW : null;
  const priority = complaint ? priorityConfig[complaint.priority] || priorityConfig.MEDIUM : null;

  return (
    <div
      className="min-h-screen bg-white"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <Navigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#004D40] py-12 text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent"></div>
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="flex items-center gap-4">
            <Link
              to="/complaints/my"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm transition-all hover:bg-white/30"
            >
              <i className="ri-arrow-left-line text-lg"></i>
            </Link>
            <div>
              <h1 className="text-xl font-bold md:text-2xl">
                {isRTL ? 'تفاصيل الشكوى' : 'Complaint Details'}
              </h1>
              {complaint && (
                <p className="mt-1 font-mono text-sm text-[#DDEEEB]">
                  #{complaint.id.slice(-8).toUpperCase()}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <main className="py-8">
        <div className="mx-auto max-w-4xl px-6 sm:px-8 lg:px-12">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
              <i className="ri-error-warning-line text-4xl text-rose-500"></i>
              <p className="mt-2 text-rose-700">{error}</p>
              <Button onClick={() => navigate('/complaints/my')} className="mt-4" variant="secondary">
                {isRTL ? 'العودة إلى شكواي' : 'Back to My Complaints'}
              </Button>
            </div>
          )}

          {/* Complaint Details */}
          {!loading && !error && complaint && (
            <div className="space-y-6">
              {/* Status & Priority Header */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium ${status?.bgColor} ${status?.color}`}
                  >
                    {isRTL ? status?.ar : status?.en}
                  </span>
                  <span className={`text-sm font-medium ${priority?.color}`}>
                    <i className="ri-flag-line mr-1"></i>
                    {isRTL ? priority?.ar : priority?.en}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-slate-500">
                    <i className={`${category?.icon}`}></i>
                    {isRTL ? category?.ar : category?.en}
                  </span>
                </div>

                <h2 className="mt-4 text-2xl font-bold text-[var(--pv-color-primary)]">{complaint.subject}</h2>

                <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {isRTL ? 'تاريخ التقديم:' : 'Submitted:'} {formatDate(complaint.createdAt)}
                  </span>
                  {complaint.resolvedAt && (
                    <span className="flex items-center gap-1">
                      <i className="ri-check-double-line"></i>
                      {isRTL ? 'تاريخ الحل:' : 'Resolved:'} {formatDate(complaint.resolvedAt)}
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="mb-3 text-lg font-semibold text-slate-900">
                  {isRTL ? 'تفاصيل الشكوى' : 'Complaint Description'}
                </h3>
                <p className="whitespace-pre-wrap text-slate-600">{complaint.description}</p>
              </div>

              {/* Linked Entities */}
              {(complaint.property || complaint.agent || complaint.company) && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="mb-4 text-lg font-semibold text-slate-900">
                    {isRTL ? 'مرتبط بـ' : 'Related To'}
                  </h3>
                  <div className="space-y-3">
                    {complaint.property && (
                      <Link
                        to={`/property/${complaint.property.slug}`}
                        className="flex items-center gap-3 rounded-xl bg-slate-50 p-4 transition-all hover:bg-slate-100"
                      >
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--pv-color-surface-sunken)]">
                          <i className="ri-home-line text-xl text-[#004D40]"></i>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{complaint.property.title}</p>
                          <p className="text-sm text-slate-500">
                            {formatAmount(complaint.property.price)} {t('currency.short')}
                          </p>
                        </div>
                      </Link>
                    )}
                    {complaint.agent && (
                      <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--pv-color-surface-sunken)]">
                          {complaint.agent.avatarUrl ? (
                            <img
                              src={complaint.agent.avatarUrl}
                              alt=""
                              className="h-12 w-12 rounded-lg object-cover"
                            />
                          ) : (
                            <i className="ri-user-line text-xl text-[#004D40]"></i>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{complaint.agent.displayName}</p>
                          <p className="text-sm text-slate-500">{isRTL ? 'وكيل عقاري' : 'Real Estate Agent'}</p>
                        </div>
                      </div>
                    )}
                    {complaint.company && (
                      <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--pv-color-surface-sunken)]">
                          {complaint.company.logoUrl ? (
                            <img
                              src={complaint.company.logoUrl}
                              alt=""
                              className="h-12 w-12 rounded-lg object-cover"
                            />
                          ) : (
                            <i className="ri-building-line text-xl text-[#004D40]"></i>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{complaint.company.name}</p>
                          <p className="text-sm text-slate-500">{isRTL ? 'شركة عقارية' : 'Real Estate Company'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Resolution Notes */}
              {complaint.resolutionNotes && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <i className="ri-check-double-line text-xl"></i>
                    <h3 className="text-lg font-semibold">
                      {isRTL ? 'تفاصيل الحل' : 'Resolution Details'}
                    </h3>
                  </div>
                  <p className="mt-3 text-emerald-800">{complaint.resolutionNotes}</p>
                </div>
              )}

              {/* Rejection Reason */}
              {complaint.rejectionReason && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6">
                  <div className="flex items-center gap-2 text-rose-700">
                    <i className="ri-close-circle-line text-xl"></i>
                    <h3 className="text-lg font-semibold">
                      {isRTL ? 'سبب الرفض' : 'Rejection Reason'}
                    </h3>
                  </div>
                  <p className="mt-3 text-rose-800">{complaint.rejectionReason}</p>
                </div>
              )}

              {/* Timeline */}
              {complaint.updates && complaint.updates.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="mb-4 text-lg font-semibold text-slate-900">
                    {isRTL ? 'سجل التحديثات' : 'Update History'}
                  </h3>
                  <div className="space-y-4">
                    {complaint.updates.map((update, index) => {
                      const updateStatus = statusConfig[update.toStatus];
                      return (
                        <div
                          key={update.id}
                          className={`relative flex gap-4 ${
                            index !== complaint.updates.length - 1
                              ? 'pb-4 before:absolute before:bottom-0 before:left-5 before:top-10 before:w-0.5 before:bg-slate-200'
                              : ''
                          }`}
                        >
                          <div
                            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
                              updateStatus?.bgColor || 'bg-slate-100'
                            }`}
                          >
                            <i className="ri-time-line text-lg text-slate-600"></i>
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              {update.fromStatus && (
                                <>
                                  <span className="text-sm text-slate-500">
                                    {isRTL
                                      ? statusConfig[update.fromStatus]?.ar
                                      : statusConfig[update.fromStatus]?.en}
                                  </span>
                                  <i className="ri-arrow-right-s-line text-slate-400"></i>
                                </>
                              )}
                              <span
                                className={`text-sm font-medium ${updateStatus?.color || 'text-slate-700'}`}
                              >
                                {isRTL ? updateStatus?.ar : updateStatus?.en}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-slate-600">
                              {isRTL && update.messageAr ? update.messageAr : update.message}
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                              {formatDate(update.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Satisfaction Rating */}
              {['RESOLVED', 'CLOSED'].includes(complaint.status) && !complaint.satisfactionRating && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
                  {!showRating ? (
                    <div className="text-center">
                      <i className="ri-star-smile-line text-4xl text-amber-500"></i>
                      <h3 className="mt-2 text-lg font-semibold text-amber-900">
                        {isRTL ? 'كيف كانت تجربتك؟' : 'How was your experience?'}
                      </h3>
                      <p className="mt-1 text-sm text-amber-700">
                        {isRTL
                          ? 'ساعدنا على التحسين بتقييم تجربتك'
                          : 'Help us improve by rating your experience'}
                      </p>
                      <Button onClick={() => setShowRating(true)} className="mt-4 bg-amber-500 hover:bg-amber-600">
                        <i className="ri-star-line mr-2"></i>
                        {isRTL ? 'قيم الآن' : 'Rate Now'}
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-lg font-semibold text-amber-900">
                        {isRTL ? 'قيم تجربتك' : 'Rate Your Experience'}
                      </h3>
                      <div className="mt-4 flex justify-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setRating(star)}
                            className="text-3xl transition-transform hover:scale-110"
                          >
                            <i
                              className={`ri-star-fill ${
                                star <= rating ? 'text-amber-400' : 'text-gray-300'
                              }`}
                            ></i>
                          </button>
                        ))}
                      </div>
                      <textarea
                        placeholder={isRTL ? 'أضف تعليقاً (اختياري)' : 'Add a comment (optional)'}
                        value={ratingComment}
                        onChange={(e) => setRatingComment(e.target.value)}
                        rows={3}
                        className="mt-4 w-full rounded-xl border border-amber-300 bg-white px-4 py-3 text-sm text-slate-900 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                      />
                      <div className="mt-4 flex gap-2">
                        <Button
                          onClick={handleSubmitRating}
                          disabled={!rating || submittingRating}
                          className="flex-1 bg-amber-500 hover:bg-amber-600"
                        >
                          {submittingRating ? (
                            <i className="ri-loader-4-line animate-spin"></i>
                          ) : (
                            isRTL ? 'إرسال التقييم' : 'Submit Rating'
                          )}
                        </Button>
                        <Button onClick={() => setShowRating(false)} variant="secondary">
                          {isRTL ? 'إلغاء' : 'Cancel'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Already Rated */}
              {complaint.satisfactionRating && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="mb-3 text-lg font-semibold text-slate-900">
                    {isRTL ? 'تقييمك' : 'Your Rating'}
                  </h3>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={`ri-star-fill text-2xl ${
                          star <= complaint.satisfactionRating! ? 'text-amber-400' : 'text-gray-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  {complaint.satisfactionComment && (
                    <p className="mt-3 text-slate-600">"{complaint.satisfactionComment}"</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
